/**
 * Accounting Integrity module — orchestrates scan, fix guide, repair tools, and audit trail.
 * Data loads per tab (lazy); scan runs one request at a time; findings review one-by-one.
 */
import { useEffect, useMemo, useState } from 'react';
import swal from 'sweetalert';
import AccountingFixProcessingOverlay, { useAccountingProcessingGuard } from './AccountingFixProcessingOverlay';
import AccountingFixWizardPanel from './AccountingFixWizardPanel';
import { buildAccountingApplyParams } from './accountingApplyParams';
import { showAccountingApplyFailure } from './accountingSwal';
import {
  applyPumpAccountingFix,
  fetchPumpAccountingCase,
  fetchPumpAccountingScan,
  friendlyFetchError,
  previewPumpAccountingFix,
  revertPumpAccountingCase,
} from './adminApi';
import AdminPumpReportExportButton from './AdminPumpReportExportButton';
import { ADMIN_PUMP_EXPORT_IDS } from './adminPumpReportSections';
import { planPostCorrectionRefresh, previewParamsFingerprint } from './accountingRefreshUtils';
import { ACCOUNTING_WORKFLOW, STANDALONE_WIZARD_KEY } from './accounting/accountingConstants';
import AccountingWorkflowNav from './accounting/AccountingWorkflowNav';
import AccountingStatusBar from './accounting/AccountingStatusBar';
import AccountingScanSection from './accounting/AccountingScanSection';
import AccountingPlaybookSection from './accounting/AccountingPlaybookSection';
import AccountingToolsSection from './accounting/AccountingToolsSection';
import AccountingHistorySection from './accounting/AccountingHistorySection';
import AccountingAuditFullReport from './accounting/AccountingAuditFullReport';
import { useAccountingModule } from './accounting/useAccountingModule';
import { isWacFixType, useWacApplyProgressPoll } from './accounting/useWacApplyProgressPoll';

const AdminPumpAccountingPanel = ({ pumpId, pumpName = '', defaultFromDate = '', defaultToDate = '', onOpenOwnerFundAnalytics }) => {
  const [fromDate, setFromDate] = useState(defaultFromDate || '');
  const [toDate, setToDate] = useState(defaultToDate || '');
  const [scan, setScan] = useState(null);
  const [loadingScan, setLoadingScan] = useState(false);
  const [activeSection, setActiveSection] = useState('monitor');
  const [findingIndex, setFindingIndex] = useState(0);
  const [expandedTechnicalKey, setExpandedTechnicalKey] = useState(null);

  const [activeFix, setActiveFix] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewFingerprint, setPreviewFingerprint] = useState(null);
  const [needsFullScanAfterFix, setNeedsFullScanAfterFix] = useState(false);
  const [applyReason, setApplyReason] = useState('');
  const [applying, setApplying] = useState(false);
  const [previewingFix, setPreviewingFix] = useState(false);
  const [reverting, setReverting] = useState(false);
  const [selectedCase, setSelectedCase] = useState(null);
  const [lastVerification, setLastVerification] = useState(null);
  const [activeFindingKey, setActiveFindingKey] = useState(null);

  const module = useAccountingModule(pumpId);
  const wacApplying = applying && isWacFixType(activeFix?.fix_type);
  const wacProgress = useWacApplyProgressPoll(pumpId, wacApplying);

  const wacProgressDetail = wacProgress?.events_done != null && wacProgress?.events_total
    ? `${wacProgress.events_done} / ${wacProgress.events_total} fuel events processed`
    : (wacProgress?.step && wacProgress?.total_steps
      ? `Step ${wacProgress.step} of ${wacProgress.total_steps}`
      : null);

  const isProcessing = applying || reverting || loadingScan || previewingFix;
  const processingMode = applying
    ? (wacApplying ? 'wac_apply' : 'apply')
    : (reverting ? 'revert' : (previewingFix ? 'preview' : 'scan'));

  const processingLabel = applying
    ? `Applying fix: ${activeFix?.label || 'ledger correction'}`
    : (reverting
      ? 'Undoing previous fix'
      : (previewingFix
        ? `Previewing: ${activeFix?.label || 'repair'}`
        : (loadingScan ? 'Integrity scan running on server' : null)));

  useAccountingProcessingGuard(
    applying || reverting,
    'An accounting fix is in progress. If you leave now, the fix may still finish on the server — open Audit trail before trying again.'
  );

  useAccountingProcessingGuard(
    loadingScan,
    'Integrity scan is still running. Leaving now may show incomplete results.'
  );

  useEffect(() => {
    setFromDate(defaultFromDate || '');
    setToDate(defaultToDate || '');
  }, [defaultFromDate, defaultToDate]);

  useEffect(() => {
    module.onSectionEnter(activeSection);
  }, [activeSection, pumpId]);

  const fixesByCategory = useMemo(() => {
    const map = {};
    module.fixes.forEach((f) => {
      const cat = f.category || 'other';
      if (!map[cat]) map[cat] = [];
      map[cat].push(f);
    });
    return map;
  }, [module.fixes]);

  const findingCount = useMemo(
    () => (scan?.sections || []).reduce((n, s) => n + (s.findings?.length || 0), 0),
    [scan]
  );

  function handleSectionChange(sectionId) {
    setActiveSection(sectionId);
  }

  function runScan() {
    setLoadingScan(true);
    setScan(null);
    setFindingIndex(0);
    module.pushActivity(`Starting integrity scan (${fromDate} to ${toDate})…`);

    fetchPumpAccountingScan(pumpId, fromDate, toDate)
      .then(async (res) => {
        if (!res.status) {
          await swal('Scan failed', res.message || 'Unable to check accounts', 'error');
          module.pushActivity('Integrity scan failed', 'error');
          return;
        }
        setScan(res);
        setLastVerification(null);
        const count = (res.sections || []).reduce((n, s) => n + (s.findings?.length || 0), 0);
        module.pushActivity(
          count > 0 ? `Scan complete — ${count} issue(s) found` : 'Scan complete — no issues found',
          count > 0 ? 'warn' : 'success'
        );
        module.loadCatalog();
      })
      .catch(async (err) => {
        await swal('Scan failed', friendlyFetchError(err), 'error');
        module.pushActivity(`Scan error: ${friendlyFetchError(err)}`, 'error');
      })
      .finally(() => setLoadingScan(false));
  }

  function resolveFixMeta(fix) {
    const fixType = fix.fix_type || fix.fixType;
    return module.fixes.find((f) => f.fix_type === fixType) || fix;
  }

  function openFixWizard(fix, finding = null, extraParams = {}, findingKey = null) {
    const params = { ...(fix.params || {}), ...extraParams };
    if (fromDate && !params.from_date) params.from_date = fromDate;
    if (toDate && !params.to_date) params.to_date = toDate;
    if (finding?.entity_type) params.entity_type = finding.entity_type;
    if (finding?.entity_id) params.entity_id = finding.entity_id;
    if (finding?.finding_type) params.finding_type = finding.finding_type;
    if (finding?.context) params.scan_context = finding.context;

    const catalogFix = resolveFixMeta(fix);
    const label = fix.plain_label || catalogFix.plain_label || fix.label || catalogFix.label;

    setActiveFix({
      fix_type: fix.fix_type || fix.fixType,
      label,
      plain_description: catalogFix.plain_description || catalogFix.description,
      what_happens: catalogFix.what_happens,
      when_to_use: catalogFix.when_to_use,
      params,
      finding_type: finding?.finding_type || null,
      risk: catalogFix.risk || 'medium',
      requires_audit_id: catalogFix.requires_audit_id,
      manual_only: Boolean(catalogFix.manual_only || fix.manual_only),
    });
    setPreview(null);
    setPreviewFingerprint(null);
    setApplyReason('');
    setActiveFindingKey(findingKey ?? STANDALONE_WIZARD_KEY);
    module.loadCatalog();
  }

  function closeFixWizard() {
    setActiveFix(null);
    setPreview(null);
    setPreviewFingerprint(null);
    setApplyReason('');
    setActiveFindingKey(null);
  }

  function handleFixChange(updates) {
    setActiveFix((prev) => {
      if (!prev) return prev;
      const next = { ...prev, ...updates, params: { ...prev.params, ...(updates.params || {}) } };
      if (previewFingerprint && previewFingerprint !== previewParamsFingerprint(next.fix_type, next.params)) {
        setPreview(null);
        setPreviewFingerprint(null);
      }
      return next;
    });
  }

  function refreshAfterCorrection(res) {
    const { scanPatch, needsFullScan, verification } = planPostCorrectionRefresh(res, scan);
    setLastVerification(verification || null);
    module.refreshAfterFix();
    if (scanPatch) {
      setScan(scanPatch);
      setNeedsFullScanAfterFix(false);
    } else if (needsFullScan) {
      setNeedsFullScanAfterFix(true);
    } else {
      setNeedsFullScanAfterFix(false);
    }
  }

  function renderFixWizardPanel() {
    if (!activeFix) return null;

    return (
      <AccountingFixWizardPanel
        activeFix={activeFix}
        preview={preview}
        previewLoading={previewingFix}
        previewStale={Boolean(
          preview
          && previewFingerprint
          && activeFix
          && previewFingerprint !== previewParamsFingerprint(activeFix.fix_type, activeFix.params)
        )}
        applyReason={applyReason}
        applying={applying}
        isProcessing={isProcessing}
        onClose={closeFixWizard}
        onPreview={runPreview}
        onApplyReasonChange={setApplyReason}
        onApply={runApply}
        onFixChange={handleFixChange}
      />
    );
  }

  function runPreview() {
    if (!activeFix) return;
    setPreviewingFix(true);
    module.pushActivity(`Previewing: ${activeFix.label}…`);
    previewPumpAccountingFix(pumpId, activeFix.fix_type, activeFix.params)
      .then((res) => {
        setPreview(res);
        setPreviewFingerprint(previewParamsFingerprint(activeFix.fix_type, activeFix.params));
        if (!res.status) {
          swal('Preview failed', res.message || 'Unable to preview', 'error');
          module.pushActivity('Preview failed', 'error');
        } else {
          module.pushActivity('Preview ready — review before applying', 'success');
        }
      })
      .catch((err) => {
        swal('Preview failed', friendlyFetchError(err), 'error');
        module.pushActivity('Preview failed', 'error');
      })
      .finally(() => setPreviewingFix(false));
  }

  function runApply() {
    if (!activeFix) return;
    if ((applyReason || '').trim().length < 10) {
      swal('Reason required', 'Please write a short note (at least 10 characters) explaining why you are applying this fix.', 'warning');
      return;
    }

    const rowCount = preview?.rows_to_change ?? 0;
    const largeFixHint = rowCount >= 50
      ? `\n\nAbout ${rowCount} rows — this may take 1–3 minutes. Please wait; do not close the page.`
      : '';

    swal({
      title: 'Apply this fix?',
      text: `${activeFix.label} — a permanent audit case will be saved.${largeFixHint}`,
      icon: 'warning',
      buttons: true,
      dangerMode: activeFix.risk === 'high',
    }).then(async (ok) => {
      if (!ok) return;
      setApplying(true);
      module.pushActivity(`Applying: ${activeFix.label}…`);
      const applyParams = buildAccountingApplyParams(activeFix.params, preview);
      try {
        const res = await applyPumpAccountingFix(
          pumpId,
          activeFix.fix_type,
          applyParams,
          applyReason.trim(),
          activeFix.finding_type
        );
        setApplying(false);

        if (res.status) {
          const verification = res.verification || res.apply_result?.verification;
          setLastVerification(verification || null);
          const plain = verification?.plain;
          const safety = res.safety?.on_disconnect;
          const msg = plain
            ? `${plain.headline}\n\n${plain.detail}\n\n${plain.remaining}`
            : res.message + (verification?.verified_ok ? '\n\nRe-check passed.' : '\n\nSome issues may remain — scan again.');
          const auditMsg = res.case?.id
            ? `Audit case #${res.case.id} saved. Your reason, user identity, and every changed row are recorded permanently.`
            : 'This correction is recorded in the audit trail.';
          await swal(
            'Correction applied',
            `${auditMsg}\n\n${msg}${safety ? `\n\n${safety}` : ''}`,
            verification?.verified_ok ? 'success' : 'warning'
          );
          if (res.case) setSelectedCase(res.case);
          closeFixWizard();
          setActiveSection('history');
          module.pushActivity(`Fix applied — audit case #${res.case?.id || '?'}`, 'success');
          refreshAfterCorrection(res);
        } else {
          await showAccountingApplyFailure(pumpId, res, 'Fix failed');
          module.pushActivity('Fix apply failed', 'error');
        }
      } catch (err) {
        setApplying(false);
        await swal(
          'Fix interrupted or failed',
          `${friendlyFetchError(err)}\n\nIf you lost connection, open Audit trail before applying again — the fix may have completed on the server.`,
          'error'
        );
        module.pushActivity('Fix interrupted', 'error');
      }
    });
  }

  function openCaseDetail(caseId) {
    fetchPumpAccountingCase(pumpId, caseId)
      .then((res) => (res.status ? setSelectedCase(res.case) : swal('Error', res.message, 'error')))
      .catch((err) => swal('Error', err.message, 'error'));
  }

  function runRevert(caseRecord, reason) {
    swal({
      title: 'Undo this fix?',
      text: 'Values will be restored to before this fix was applied.',
      icon: 'warning',
      buttons: true,
      dangerMode: true,
    }).then(async (ok) => {
      if (!ok) return;
      setReverting(true);
      module.pushActivity(`Undoing audit case #${caseRecord.id}…`);
      try {
        const res = await revertPumpAccountingCase(pumpId, caseRecord.id, reason);
        setReverting(false);

        if (res.status) {
          setSelectedCase(res.case || null);
          await swal('Undone', res.message || 'Fix was reverted', 'success');
          module.pushActivity(`Audit case #${caseRecord.id} undone`, 'success');
          refreshAfterCorrection(res);
        } else {
          await swal('Undo failed', res.message || 'Could not revert fix', 'error');
        }
      } catch (err) {
        setReverting(false);
        await swal('Undo interrupted or failed', `${friendlyFetchError(err)}\n\nCheck Audit trail for the result.`, 'error');
      }
    });
  }

  const intro = scan?.intro;
  const health = scan?.health;
  const healthPlain = health?.plain;
  const playbook = scan?.playbook;
  const sections = scan?.sections || [];

  return (
    <div className={`accounting-panel accounting-module${isProcessing ? ' is-processing' : ''}`} id={ADMIN_PUMP_EXPORT_IDS.accounting}>
      <AccountingFixProcessingOverlay
        visible={isProcessing}
        mode={processingMode}
        title={
          applying
            ? 'Applying fix — please wait'
            : (reverting
              ? 'Undoing fix — please wait'
              : (previewingFix
                ? 'Previewing fix — please wait'
                : 'Running integrity scan — please wait'))
        }
        message={
          applying
            ? (wacProgress?.message || activeFix?.label || 'Recalculating purchase cost on fuel sales.')
            : (reverting
              ? 'Restoring values from before this fix.'
              : (previewingFix
                ? 'Read-only preview — counting rows that would change.'
                : 'Checking balance sheet, profit, fuel costs, stock, gas, and cash for this period.'))
        }
        livePhase={wacApplying ? (wacProgress?.message || null) : null}
        progressDetail={wacApplying ? wacProgressDetail : null}
        safetyNote={
          applying
            ? 'All changes run in one database transaction. If anything fails, nothing is saved. Check Audit trail before retrying.'
            : (reverting
              ? 'Undo runs in one transaction — either all values restore or none do.'
              : (previewingFix
                ? 'Preview is read-only — no ledger changes are made.'
                : 'Scan is read-only — no ledger changes are made.'))
        }
      />

      <div className="admin-pump-accounting-audit-notice mb-3">
        <strong>How this module works</strong>
        <p className="small mb-0 mt-1">
          Nothing loads until you need it: run a <strong>scan</strong> first, review issues <strong>one at a time</strong>,
          use <strong>Repair tools</strong> only for specialist scripts, and check <strong>Audit trail</strong> for every change.
        </p>
      </div>

      <AccountingStatusBar
        activeSection={activeSection}
        scan={scan}
        loadingScan={loadingScan}
        loadingCatalog={module.loadingCatalog}
        loadingTools={module.loadingTools}
        loadingHistory={module.loadingHistory}
        catalogReady={module.catalogReady}
        toolsReady={module.toolsReady}
        historyReady={module.historyReady}
        activityLog={module.activityLog}
        processingLabel={processingLabel}
      />

      <div className="d-flex flex-wrap justify-content-end mb-3">
        <AdminPumpReportExportButton
          targetId={ADMIN_PUMP_EXPORT_IDS.accountingFullAudit}
          filename={`pump-${pumpId}-accounting-audit-${fromDate || 'scan'}-to-${toDate || 'scan'}.pdf`}
          label="Export full audit PDF"
          disabled={!scan || !fromDate || !toDate}
          metadata={{
            title: `${pumpName || 'Pump'} — Full accounting audit`,
            subtitle: fromDate && toDate ? `${fromDate} to ${toDate} · ${findingCount} finding(s)` : '',
          }}
          title="Exporting full audit PDF…"
          message="Building PDF with every finding and fix guide step from your last scan. Large pumps may take a minute."
          missingTargetMessage="Run an integrity scan first, then export again."
          chunkSelector=".accounting-audit-pdf-chunk"
        />
      </div>

      <div
        id={ADMIN_PUMP_EXPORT_IDS.accountingFullAudit}
        className="admin-pump-accounting-full-audit-export"
        aria-hidden={!scan}
      >
        {scan && (
          <AccountingAuditFullReport
            pumpId={pumpId}
            pumpName={pumpName}
            fromDate={fromDate}
            toDate={toDate}
            scan={scan}
          />
        )}
      </div>

      <AccountingWorkflowNav
        activeSection={activeSection}
        onChange={handleSectionChange}
        disabled={isProcessing}
        scanFindingCount={findingCount}
      />

      <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
        <p className="small text-muted mb-0 flex-grow-1">
          {ACCOUNTING_WORKFLOW.find((s) => s.id === activeSection)?.hint}
        </p>
        {onOpenOwnerFundAnalytics && (
          <button
            type="button"
            className="btn btn-sm btn-outline-primary"
            onClick={onOpenOwnerFundAnalytics}
            disabled={isProcessing}
          >
            Owner&apos;s Fund Analytics →
          </button>
        )}
      </div>

      {activeSection === 'monitor' && (
        <AccountingScanSection
          intro={intro}
          health={health}
          healthPlain={healthPlain}
          sections={sections}
          scan={scan}
          fromDate={fromDate}
          toDate={toDate}
          onFromDateChange={setFromDate}
          onToDateChange={setToDate}
          onRunScan={runScan}
          loadingScan={loadingScan}
          lastVerification={lastVerification}
          needsFullScanAfterFix={needsFullScanAfterFix}
          selectedCase={selectedCase}
          onCloseCase={() => setSelectedCase(null)}
          onRevertCase={(reason) => runRevert(selectedCase, reason)}
          reverting={reverting}
          findingIndex={findingIndex}
          onFindingIndexChange={setFindingIndex}
          expandedTechnicalKey={expandedTechnicalKey}
          onToggleTechnical={setExpandedTechnicalKey}
          onFix={(fix, finding, key) => openFixWizard(fix, finding, {}, key)}
          activeFindingKey={activeFindingKey}
          renderFixWizard={renderFixWizardPanel}
          catalogError={module.errors.catalog}
          onLoadCatalog={module.loadCatalog}
          loadingCatalog={module.loadingCatalog}
        />
      )}

      {activeSection === 'playbook' && (
        <AccountingPlaybookSection
          playbook={playbook}
          scanRan={Boolean(scan)}
          onStartFix={(fix) => openFixWizard(fix, null, {}, STANDALONE_WIZARD_KEY)}
          renderFixWizard={renderFixWizardPanel}
          activeFindingKey={activeFindingKey}
        />
      )}

      {activeSection === 'tools' && (
        <AccountingToolsSection
          pumpId={pumpId}
          fromDate={fromDate}
          toDate={toDate}
          fixesByCategory={fixesByCategory}
          fixesIntro={module.fixesIntro}
          repairScripts={module.repairScripts}
          repairScriptsIntro={module.repairScriptsIntro}
          wacAudits={module.wacAudits}
          loadingTools={module.loadingTools}
          toolsError={module.errors.tools}
          onRetryLoad={module.loadToolsData}
          disabled={isProcessing}
          onOpenFixWizard={(fix, extra = {}) => openFixWizard(fix, null, extra, STANDALONE_WIZARD_KEY)}
          renderFixWizard={renderFixWizardPanel}
          activeFindingKey={activeFindingKey}
          onPreviewStart={() => setPreviewingFix(true)}
          onPreviewEnd={() => setPreviewingFix(false)}
          onApplyStart={(label) => {
            setApplying(true);
            setActiveFix({ fix_type: 'tool', label: label || 'Repair tool' });
          }}
          onApplyEnd={() => {
            setApplying(false);
            setActiveFix(null);
          }}
          onCompleted={refreshAfterCorrection}
          onOpenHistory={() => handleSectionChange('history')}
        />
      )}

      {activeSection === 'history' && (
        <AccountingHistorySection
          stats={module.stats}
          cases={module.cases}
          casesPagination={module.casesPagination}
          caseFilter={module.caseFilter}
          onCaseFilterChange={(value) => {
            module.setCaseFilter(value);
            module.loadCases(1, value);
          }}
          onLoadCases={module.loadCases}
          fixes={module.fixes}
          loadingHistory={module.loadingHistory}
          historyError={module.errors.history}
          onRetryLoad={() => module.loadHistoryData(1, module.caseFilter)}
          selectedCase={selectedCase}
          onOpenCase={openCaseDetail}
          onCloseCase={() => setSelectedCase(null)}
          onRevertCase={(reason) => runRevert(selectedCase, reason)}
          reverting={reverting}
        />
      )}
    </div>
  );
};

export default AdminPumpAccountingPanel;
